import { useState } from "react"
import { ChromePicker } from "react-color"

import Style from "./style"

import useColorCookie from "shared/model/useColorCookie"
import useThemeCookie, { BASIC, GRADATION, TRANSPARENCY } from "shared/model/useThemeCookie"
import useLogoTransparency from "shared/model/useLogoTransparency"
import { defaultLogoDimensions } from "shared/model/useLogoStorage"

const SettingModal = ({ closeEvent, logoStorage, setLogoStorage, logoImg, navigationBlock, setNavigationBlock }) => {
    const [themeCookie, setThemeCookie] = useThemeCookie()
    const [colorCookie, setColorCookie] = useColorCookie()
    const [logoTransparency, setLogoTransparency] = useLogoTransparency()

    const nowBgColor = colorCookie?.basic.bg
    const nowFontColor = colorCookie?.basic.font
    const nowBreakTimerColor = colorCookie?.basic.breakTimer || '#4FD1C5'

    const nowBgColor1 = colorCookie?.gradation.bg1
    const nowBgColor2 = colorCookie?.gradation.bg2
    const nowBgColor3 = colorCookie?.gradation.bg3
    const nowBgColor4 = colorCookie?.gradation.bg4

    const [themeType, setThemeType] = useState(themeCookie)
    const [transparency, setTransparency] = useState(logoTransparency ?? 50)

    const [bgColor, setBgColor] = useState(nowBgColor)
    const [fontColor, setFontColor] = useState(nowFontColor)
    const [breakTimerColor, setBreakTimerColor] = useState(nowBreakTimerColor)

    const [bg1Color, setBg1Color] = useState(nowBgColor1)
    const [bg2Color, setBg2Color] = useState(nowBgColor2)
    const [bg3Color, setBg3Color] = useState(nowBgColor3)
    const [bg4Color, setBg4Color] = useState(nowBgColor4)

    const [logoWidth, setLogoWidth] = useState(logoStorage?.width || defaultLogoDimensions.width)
    const [logoHeight, setLogoHeight] = useState(logoStorage?.height || defaultLogoDimensions.height)



    const bgChangeEvent = (color) => {
        setBgColor(color.hex)
    }

    const fontChangeEvent = (color) => {
        setFontColor(color.hex)
    }

    const breakTimerChangeEvent = (color) => {
        setBreakTimerColor(color.hex)
    }

    const bg1ChangeEvent = (color) => {
        setBg1Color(color.hex)
    }

    const bg2ChangeEvent = (color) => {
        setBg2Color(color.hex)
    }

    const bg3ChangeEvent = (color) => {
        setBg3Color(color.hex)
    }

    const bg4ChangeEvent = (color) => {
        setBg4Color(color.hex)
    }

    const initEvent = () => {
        if (themeType === BASIC) {
            setBgColor("#ffffff")
            setFontColor("#1F2024")
            setBreakTimerColor("#4FD1C5")
        }
        else if (themeType === GRADATION) {
            setBg1Color("#4DBFFF")
            setBg2Color("#E6FF4D")
            setBg3Color("#4DFF89")
            setBg4Color("#FF4D77")
        }
        else if (themeType === TRANSPARENCY) {
            setTransparency(50)
        }

        // Reset logo dimensions to default
        setLogoStorage({
            width: defaultLogoDimensions.width,
            height: defaultLogoDimensions.height
        })
    }

    const applyEvent = () => {

        if (themeType === BASIC) {
            setColorCookie({
                ...colorCookie,
                "type": BASIC,
                "basic": {
                    "bg": bgColor,
                    "font": fontColor,
                    "breakTimer": breakTimerColor,
                }
            })
            setThemeCookie(BASIC)
        }

        else if (themeType === GRADATION) {
            setColorCookie({
                ...colorCookie,
                "type": GRADATION,
                "gradation": {
                    "bg": "#1F2024",
                    "font": "#ffffff",
                    "bg1": bg1Color,
                    "bg2": bg2Color,
                    "bg3": bg3Color,
                    "bg4": bg4Color,
                }
            })
            setThemeCookie(GRADATION)
        }

        else if (themeType === TRANSPARENCY) {
            setLogoTransparency(transparency)
        }

        // Save logo dimensions to localStorage
        setLogoStorage({
            width: logoWidth,
            height: logoHeight
        })

        closeEvent()
    }

    const handleTransparencySliderChange = (e) => {
        setTransparency(Number(e.target.value))
    }

    const handleTransparencyInputChange = (e) => {
        let value = e.target.value.replace('%', '')
        value = Number(value)
        if (value >= 0 && value <= 100) {
            setTransparency(value)
        }
    }

    return (
        <Style.Bg>

            <Style.Modal>
                <h2>Theme</h2>

                <Style.RadioDiv>
                    <Style.Radio $select={themeType === BASIC} onClick={() => setThemeType(BASIC)}>Simple</Style.Radio>
                    <Style.Radio $select={themeType === GRADATION} onClick={() => setThemeType(GRADATION)}>Gradation</Style.Radio>
                    <Style.Radio $select={themeType === TRANSPARENCY} onClick={() => setThemeType(TRANSPARENCY)}>Store logo</Style.Radio>
                </Style.RadioDiv>

                {themeType === TRANSPARENCY ? (
                    <Style.TransparencySection>
                        {logoImg && (
                            <Style.LogoPreview $opacity={transparency}>
                                <img src={logoImg} alt="logo preview" />
                            </Style.LogoPreview>
                        )}
                        <Style.TransparencyLabel>Logo appearance</Style.TransparencyLabel>
                        <Style.TransparencyControls>
                            <Style.TransparencySlider
                                type="range"
                                min="0"
                                max="100"
                                value={transparency}
                                onChange={handleTransparencySliderChange}
                            />
                            <Style.TransparencyInput
                                type="text"
                                value={`${transparency}%`}
                                onChange={handleTransparencyInputChange}
                            />
                        </Style.TransparencyControls>
                    </Style.TransparencySection>
                ) : (
                    <>
                        <Style.ColorDiv>

                            {themeType === BASIC &&
                                <>
                                    <Style.ColorElem>
                                        <ChromePicker color={bgColor} onChange={bgChangeEvent} disableAlpha={true} />
                                        <p>Background Color</p>
                                    </Style.ColorElem>
                                    <Style.ColorElem>
                                        <ChromePicker color={fontColor} onChange={fontChangeEvent} disableAlpha={true} />
                                        <p>Font Color</p>
                                    </Style.ColorElem>
                                    <Style.ColorElem>
                                        <ChromePicker color={breakTimerColor} onChange={breakTimerChangeEvent} disableAlpha={true} />
                                        <p>Break Timer Color</p>
                                    </Style.ColorElem>
                                </>
                            }

                            {themeType === GRADATION &&
                                <>
                                    <Style.ColorElem>
                                        <ChromePicker color={bg1Color} onChange={bg1ChangeEvent} disableAlpha={true} />
                                        <p>Background Color 1</p>
                                    </Style.ColorElem>
                                    <Style.ColorElem>
                                        <ChromePicker color={bg2Color} onChange={bg2ChangeEvent} disableAlpha={true} />
                                        <p>Background Color 2</p>
                                    </Style.ColorElem>
                                    <Style.ColorElem>
                                        <ChromePicker color={bg3Color} onChange={bg3ChangeEvent} disableAlpha={true} />
                                        <p>Background Color 3</p>
                                    </Style.ColorElem>
                                    <Style.ColorElem>
                                        <ChromePicker color={bg4Color} onChange={bg4ChangeEvent} disableAlpha={true} />
                                        <p>Background Color 4</p>
                                    </Style.ColorElem>
                                </>
                            }

                        </Style.ColorDiv>

                        <Style.LogoSection>
                            <h3>Logo Settings</h3>
                            <Style.LogoControls>
                                <Style.LogoControl>
                                    <label>Width (px)</label>
                                    <input
                                        type="number"
                                        value={logoWidth}
                                        onChange={(e) => setLogoWidth(Number(e.target.value))}
                                        min="10"
                                        max="500"
                                    />
                                </Style.LogoControl>
                                <Style.LogoControl>
                                    <label>Height (px)</label>
                                    <input
                                        type="number"
                                        value={logoHeight}
                                        onChange={(e) => setLogoHeight(Number(e.target.value))}
                                        min="10"
                                        max="500"
                                    />
                                </Style.LogoControl>
                            </Style.LogoControls>
                        </Style.LogoSection>
                    </>
                )}

                {/* <Style.NavigationSection>
                    <h3>Navigation Settings</h3>
                    <Style.NavigationControl>
                        <label>
                            <input
                                type="checkbox"
                                checked={navigationBlock}
                                onChange={(e) => setNavigationBlock(e.target.checked)}
                            />
                            Show confirmation before leaving timer page
                        </label>
                        <p style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                            When enabled, you'll be asked to confirm before navigating away from the timer using browser back button or navigation gestures.
                        </p>
                    </Style.NavigationControl>
                </Style.NavigationSection>*/}

                <Style.ButtonDiv>
                    <Style.ApplyButton $minor onClick={closeEvent}>닫기</Style.ApplyButton>
                    <Style.ApplyButton $minor onClick={initEvent}>초기화</Style.ApplyButton>
                    <Style.ApplyButton onClick={applyEvent}>적용</Style.ApplyButton>
                </Style.ButtonDiv>

            </Style.Modal>

        </Style.Bg >
    )
}

export default SettingModal
